import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { useConfig } from "@/context/ConfigContext";
import { JODHPUR_AREAS } from "@/lib/i18n";
import { api } from "@/lib/api";
import PropertyCard from "@/components/PropertyCard";
import { Button } from "@/components/ui/button";
import { Home as HomeIcon, LandPlot, Store, Trees, ArrowRight, Search, ShieldCheck, MapPin, Boxes, Sparkles, Crown, Building2 } from "lucide-react";

const CATEGORIES = [
  { key: "ghar", icon: HomeIcon },
  { key: "plot", icon: LandPlot },
  { key: "shop", icon: Store },
  { key: "jamin", icon: Trees },
];

export default function Home() {
  const { t, lang } = useLang();
  const cfg = useConfig();
  const [properties, setProperties] = useState([]);
  const [premium, setPremium] = useState([]);
  const [builderProps, setBuilderProps] = useState([]);
  const [brokerProps, setBrokerProps] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_category: {} });

  useEffect(() => {
    api.get("/properties?limit=8&state=Rajasthan").then(({ data }) => setProperties(data)).catch(() => {});
    api.get("/properties/premium?limit=6").then(({ data }) => setPremium(data)).catch(() => {});
    api.get("/properties/from-plan/builder_monthly?limit=6").then(({ data }) => setBuilderProps(data)).catch(() => {});
    api.get("/properties/from-plan/broker_yearly?limit=6").then(({ data }) => setBrokerProps(data)).catch(() => {});
    api.get("/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 grain-bg" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6 space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary/15 border border-secondary/40 text-secondary text-xs font-semibold" data-testid="jodhpur-launch-badge">
              <Sparkles size={12} />
              <span>{lang === "hi" ? "अभी जोधपुर में लॉन्च" : "Now Live in Jodhpur"}</span>
              <span className="opacity-60">• Rajasthan</span>
            </div>
            <div className="overline text-secondary">{t.tagline}</div>
            <div className="wordmark text-6xl sm:text-7xl lg:text-7xl text-primary" data-testid="hero-wordmark">
              Bhoomi<span className="wordmark-dot" style={{ width: "10px", height: "10px", marginBottom: "18px" }}></span><span className="wordmark-mitra">Mitra</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground tracking-tight leading-[1.05]">
              {t.heroTitle}
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-lg">
              {t.heroSub}
            </p>
            <div className="flex flex-wrap gap-3 pt-2">
              <Link to="/browse">
                <Button size="lg" className="bg-primary hover:bg-primary/90 rounded-full px-6" data-testid="hero-explore-btn">
                  <Search size={16} className="mr-2" />{t.exploreBtn}
                </Button>
              </Link>
              <Link to="/sell">
                <Button size="lg" variant="outline" className="rounded-full px-6 border-2" data-testid="hero-sell-btn">
                  {t.sellBtn} <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
            </div>
            <div className="flex flex-wrap gap-6 pt-4 text-sm">
              <div className="flex items-center gap-2"><ShieldCheck size={16} className="text-primary" /> Verified Papers</div>
              <div className="flex items-center gap-2"><Boxes size={16} className="text-secondary" /> 3D Property View</div>
              <div className="flex items-center gap-2"><MapPin size={16} className="text-primary" /> Precise Map Location</div>
            </div>
          </div>
          <div className="lg:col-span-6 relative">
            <div className="aspect-[4/3] rounded-2xl overflow-hidden border border-border shadow-[0_10px_40px_rgb(0,0,0,0.08)]">
              <img
                src="https://images.pexels.com/photos/7031581/pexels-photo-7031581.jpeg"
                alt="hero"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-card border border-border rounded-xl p-4 shadow-lg max-w-[240px] hidden md:block">
              <div className="overline text-secondary mb-1">Listed</div>
              <div className="text-3xl font-bold text-primary tracking-tight">{stats.total}+</div>
              <div className="text-xs text-muted-foreground">Properties across Bharat</div>
            </div>
          </div>
        </div>
      </section>

      {/* FREE LISTING + BOOST CTA BANNER */}
      <section className="border-y border-primary/30 bg-gradient-to-r from-primary via-primary to-primary/90 text-primary-foreground" data-testid="free-listing-banner">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-white/15 grid place-items-center shrink-0">
              <Sparkles size={22} />
            </div>
            <div>
              <div className="wordmark text-2xl sm:text-3xl leading-none">
                <span className="italic text-secondary">{lang === "hi" ? "मुफ़्त लिस्टिंग" : "Free Listing"}</span>
              </div>
              <div className="text-xs sm:text-sm opacity-90 mt-1">
                {lang === "hi"
                  ? "अनगिनत प्रॉपर्टी बिल्कुल मुफ़्त लिस्ट करें। बूस्ट करके होमपेज पर लाएँ।"
                  : "List unlimited properties free. Boost them to appear on Homepage."}
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Link to="/sell">
              <button className="px-5 py-2.5 rounded-full bg-white text-primary text-sm font-semibold hover:bg-white/90 transition-opacity" data-testid="cta-list-free">
                {lang === "hi" ? "मुफ़्त लिस्ट करें" : "List for Free"}
              </button>
            </Link>
            <Link to="/plans">
              <button className="px-5 py-2.5 rounded-full bg-secondary text-secondary-foreground text-sm font-semibold hover:opacity-90 transition-opacity" data-testid="cta-view-plans">
                {lang === "hi" ? "प्लान देखें →" : "View Plans →"}
              </button>
            </Link>
          </div>
        </div>
      </section>


      {/* JODHPUR AREAS - launch focus */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="jodhpur-areas-section">
        <div className="mb-5 flex items-end justify-between flex-wrap gap-2">
          <div>
            <div className="overline text-secondary">{lang === "hi" ? "जोधपुर एवं ग्रामीण क्षेत्र" : "Jodhpur & Rural Areas"}</div>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mt-1">
              {lang === "hi" ? "अपने इलाक़े में प्रॉपर्टी खोजें" : "Find property in your area"}
            </h2>
          </div>
          <Link to="/browse?state=Rajasthan" className="text-sm font-semibold text-primary hover:underline flex items-center gap-1">
            {lang === "hi" ? "सब देखें" : "See all"} <ArrowRight size={14} />
          </Link>
        </div>
        <div className="flex flex-wrap gap-2">
          {JODHPUR_AREAS.map((area) => (
            <Link
              key={area.name}
              to={`/browse?city=${encodeURIComponent(area.name)}&state=Rajasthan`}
              className={`group inline-flex items-center gap-2 px-4 py-2.5 rounded-full border transition-all ${
                area.tag === "city" ? "bg-primary text-primary-foreground border-primary" :
                "bg-card border-border hover:border-primary hover:bg-primary/5"
              }`}
              data-testid={`area-chip-${area.name.toLowerCase()}`}
            >
              <MapPin size={13} className={area.tag === "city" ? "opacity-90" : "text-secondary"} />
              <span className="text-sm font-medium">{lang === "hi" ? area.devanagari : area.name}</span>
              {area.tag === "rural" && (
                <span className="text-[9px] uppercase tracking-wider opacity-70">
                  {lang === "hi" ? "ग्रामीण" : "Rural"}
                </span>
              )}
            </Link>
          ))}
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {CATEGORIES.map(({ key, icon: Icon }) => (
            <Link
              key={key}
              to={`/browse?category=${key}`}
              className="group border border-border bg-card rounded-xl p-6 hover:border-primary hover:-translate-y-1 transition-all"
              data-testid={`category-${key}`}
            >
              <div className="w-12 h-12 rounded-lg bg-accent grid place-items-center mb-4 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                <Icon size={22} />
              </div>
              <div className="font-semibold text-lg tracking-tight">{t.categories[key]}</div>
              <div className="text-xs text-muted-foreground mt-1">
                {stats.by_category?.[key] || 0} listings
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* PREMIUM FEATURED — homepage carousel of boosted listings */}
      {premium.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="premium-section">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="overline text-secondary flex items-center gap-1">
                <Crown size={11} className="text-secondary" /> {lang === "hi" ? "प्रीमियम फ़ीचर्ड" : "Premium Featured"}
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mt-1">
                {lang === "hi" ? "बूस्ट की गई लिस्टिंग" : "Boosted Listings"}
              </h2>
            </div>
            <Link to="/browse?featured=1" className="text-sm font-semibold text-primary hover:underline flex items-center gap-1">
              {lang === "hi" ? "सब देखें" : "View all"} <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {premium.slice(0, 6).map((p, i) => <PropertyCard key={p.id} property={p} index={i} />)}
          </div>
        </section>
      )}

      {/* BUILDER BANNER — rotating properties from active Builder plan users */}
      {builderProps.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="builder-section">
          <div className="rounded-2xl border-2 border-emerald-300 bg-emerald-50/70 p-6 sm:p-8">
            <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-emerald-700 text-white grid place-items-center">
                  <Building2 size={22} />
                </div>
                <div>
                  <div className="overline text-emerald-700">{lang === "hi" ? "बिल्डर पार्टनर्स" : "Builder Partners"}</div>
                  <h2 className="text-xl sm:text-2xl font-bold tracking-tight">
                    {lang === "hi" ? "आधिकारिक बिल्डर प्रोजेक्ट्स" : "Official Builder Projects"}
                  </h2>
                </div>
              </div>
              <Link to="/plans" className="text-xs font-semibold text-emerald-800 hover:underline flex items-center gap-1">
                {lang === "hi" ? "बिल्डर बनें" : "Become a Builder"} <ArrowRight size={12} />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {builderProps.slice(0, 3).map((p, i) => <PropertyCard key={p.id} property={p} index={i} />)}
            </div>
          </div>
        </section>
      )}

      {/* BROKERS SHOWCASE — properties from active Broker plan users */}
      {brokerProps.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="broker-section">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="overline text-secondary flex items-center gap-1">
                <ShieldCheck size={11} className="text-primary" /> {lang === "hi" ? "ब्रोकर लिस्टिंग" : "Broker Listings"}
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mt-1">
                {lang === "hi" ? "भरोसेमंद ब्रोकर से" : "From Trusted Brokers"}
              </h2>
            </div>
            <Link to="/plans" className="text-sm font-semibold text-primary hover:underline flex items-center gap-1">
              {lang === "hi" ? "ब्रोकर बनें" : "Become a Broker"} <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {brokerProps.slice(0, 3).map((p, i) => <PropertyCard key={p.id} property={p} index={i} />)}
          </div>
        </section>
      )}

      {/* LATEST LISTINGS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="overline text-secondary">Freshly listed</div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mt-1">Latest Properties</h2>
          </div>
          <Link to="/browse" className="text-sm font-semibold text-primary hover:underline flex items-center gap-1">
            View all <ArrowRight size={14} />
          </Link>
        </div>
        {properties.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-border rounded-xl bg-accent/30">
            <p className="text-muted-foreground">No listings yet. Be the first to <Link to="/sell" className="text-primary underline">list your property</Link>.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {properties.map((p, i) => <PropertyCard key={p.id} property={p} index={i} />)}
          </div>
        )}
      </section>
    </div>
  );
}
